import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

export type MethodName = "Omnifit" | "Optifit" | "Random";
type BinCounts = Record<string, number>;
export type RawDataByMethod = Record<MethodName, BinCounts>;

const methods: MethodName[] = ["Omnifit", "Optifit", "Random"];
const colors: Record<MethodName, string> = { Omnifit: "#C0392B", Optifit: "#5D6D7E", Random: "#4A7C3F" };

function buildChartData(rawData: RawDataByMethod, bins: string[]) {
  const totals: Record<string, number> = {};
  methods.forEach(m => {
    totals[m] = Object.values(rawData[m]).reduce((a: number, b: number) => a + b, 0);
  });
  const countData = bins.map(bin => ({
    bin,
    Omnifit: rawData.Omnifit[bin] ?? 0,
    Optifit: rawData.Optifit[bin] ?? 0,
    Random: rawData.Random[bin] ?? 0,
  }));
  const pctData = bins.map(bin => ({
    bin,
    Omnifit: totals.Omnifit ? +((rawData.Omnifit[bin] / totals.Omnifit) * 100).toFixed(1) : 0,
    Optifit: totals.Optifit ? +((rawData.Optifit[bin] / totals.Optifit) * 100).toFixed(1) : 0,
    Random: totals.Random ? +((rawData.Random[bin] / totals.Random) * 100).toFixed(1) : 0,
  }));
  return { totals, countData, pctData };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string }>;
  label?: string;
  mode: "pct" | "count";
  totals: Record<string, number>;
}

const CustomTooltip = ({ active, payload, label, mode, totals }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 8,
          padding: "10px 14px",
          fontSize: 11,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <p style={{ color: "#333", marginBottom: 6, fontWeight: 600 }}>HILO: {label} mm</p>
        {payload.map(p => (
          <p key={p.name} style={{ color: p.fill, margin: "2px 0" }}>
            {p.name}: {p.value}{mode === "pct" ? "%" : " joints"}
            <span style={{ color: "#666", fontSize: 9 }}> (total: {totals[p.name]})</span>
          </p>
        ))}
        <p style={{ color: "#16a34a", fontSize: 9, marginTop: 6 }}>✓ All within 1.5mm spec</p>
      </div>
    );
  }
  return null;
};

interface HILOChartProps {
  rawData?: RawDataByMethod;
  bins?: string[];
}

function hasCompleteHiloReport(raw: RawDataByMethod | undefined, binList: string[] | undefined): raw is RawDataByMethod {
  if (!raw || !binList?.length) return false;
  return methods.every((m) => {
    const counts = raw[m];
    if (!counts || typeof counts !== "object") return false;
    return binList.every((b) => typeof counts[b] === "number");
  });
}

export default function HILOChart({ rawData: propRawData, bins: propBins }: HILOChartProps) {
  const [mode, setMode] = useState<"pct" | "count">("count");

  if (!hasCompleteHiloReport(propRawData, propBins)) {
    return (
      <div
        style={{
          background: "whitesmoke",
          padding: "48px 24px",
          textAlign: "center",
          color: "#666",
          fontSize: 15,
        }}
      >
        No complete report
      </div>
    );
  }

  const rawData = propRawData;
  const bins = propBins!;

  const { totals, countData, pctData } = buildChartData(rawData, bins);
  const data = mode === "pct" ? pctData : countData;

  
  return (
    <div style={{
      background: "whitesmoke",
      minHeight: "100vh",
     // display: "flex",
     // flexDirection: "column",
     // alignItems: "center",
   //   justifyContent: "center",
      //fontFamily: "'Georgia', serif",
      padding: "10px 20px",
    }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 8, maxWidth: "100%" }}>
        <p style={{ color: "green", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
          ✓ All joints within 1.5mm HILO project constraint
        </p>
        <h1 style={{ color: "black", fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>
          Max-HiLo Distribution at the WPS HiLo limit
        </h1>
        <p style={{ color: "black", fontSize: 14, fontWeight: 500, margin: 0 }}>
          Number of joints per HiLo range — Omnifit, Optifit, and Random 
        </p>
      </div>

      {/* Total badges */}
      <div style={{ display: "flex", gap: 20, marginBottom: 28, marginTop: 16, justifyContent: "center" }}>
        {methods.map(m => (
          <div key={m} style={{
            background: "white",
            border: `2px solid ${colors[m]}`,
            borderRadius: 10,
            padding: "8px 18px",
            textAlign: "center",
          }}>
            <div style={{ color: colors[m], fontWeight: 700, fontSize: 14 }}>{m}</div>
            <div style={{ color: "black", fontSize: 18, fontWeight: 800 }}>{totals[m]}</div>
            <div style={{ color: "green", fontSize: 14, fontWeight: 700 }}>joints ✓</div>
          </div>
        ))}
      </div>

      {/* Toggle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 0, background: "white", borderRadius: 8, overflow: "hidden", border: "1px solid #333" }}>
        {([["pct", "% of Method Total"], ["count", "Raw Joint Count"]] as const).map(([val, label]) => (
          <button key={val} onClick={() => setMode(val)} style={{
            padding: "8px 20px",
            background: mode === val ? "white" : "transparent",
            color: mode === val ? "black" : "#666",
            border: "none",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: mode === val ? 700 : 400,
            borderRight: val === "pct" ? "1px solid #333" : "none",
          }}>{label}</button>
        ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ background: "white", borderRadius: 16, padding: "30px 20px 20px", width: "100%",  border: "1px solid #2a2a3a" }}>
        {/* Within spec background label */}
        <div style={{ position: "relative" }}>
          <div style={{
            position: "absolute", top: 0, left: 60, right: 20, bottom: 40,
            background: "rgba(74, 222, 128, 0.04)",
            border: "1px dashed rgba(74,222,128,0.2)",
            borderRadius: 4,
            pointerEvents: "none",
            zIndex: 0,
          }}>
            <span style={{ position: "absolute", top: 6, right: 10, color: "rgba(74,222,128,0.4)", fontSize: 9, letterSpacing: 1 }}>
              WITHIN SPEC ZONE
            </span>
          </div>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 20 }} barCategoryGap="25%" barGap={3}>
              <XAxis
                dataKey="bin"
                tick={{ fill: "black", fontSize: 14, fontWeight: 500 }}
                label={{ value: "HILO (mm)", position: "insideBottom", offset: -10, fill: "#aaa", fontSize: 11 }}
              />
              <YAxis
                tick={{ fill: "black", fontSize: 14, fontWeight: 500 }}
                label={{
                  value: mode === "pct" ? "% of Method Total" : "Number of Joints",
                  angle: -90, position: "insideLeft", offset: 10, fill: "black", fontSize: 14, fontWeight: 500
                }}
              />
              <Tooltip content={<CustomTooltip mode={mode} totals={totals} />} />
              <Legend
                wrapperStyle={{ paddingTop: 16, color: "black", fontSize: 14, fontWeight: 500 }}
                formatter={(value: string) => <span style={{ color: colors[value as MethodName] }}>{value} ({totals[value]} joints)</span>}
              />
              {methods.map(m => (
                <Bar key={m} dataKey={m} fill={colors[m]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key insight */}
      <div style={{
        marginTop: 24, background: "white", borderRadius: 12,
        padding: "16px 24px", border: "1px solid #2a2a3a", textAlign: "center"
      }}>
        <p style={{ color: "#888", fontSize: 14, fontWeight: 500, margin: 0 }}>
          <strong style={{ color: "black" }}>How to read this:</strong>{" "}
          Each bar shows how many joints fall in that HiLo range for the selected fitting method.
          Use &quot;Raw Joint Count&quot; to match the exported PDF chart.
        </p>
      </div>
    </div>
  );
}
